import { createFileRoute } from "@tanstack/react-router";
import { lazy, useState } from "react";
import { LazyBoundary } from "@/components/LazyBoundary";
import {
  checkScreenerAnswer,
  getPublicStudy,
  InterviewError,
  startPublicInterview,
} from "@/features/interview/api";
import { StartAttempts } from "@/features/interview/startAttempt";

// Lazy so the interview UI (ElevenLabs components + three.js Orb) ships as an
// on-demand chunk loaded only on this public route, not in the app-wide bundle.
const InterviewRoom = lazy(() =>
  import("@/features/interview/InterviewRoom").then((m) => ({
    default: m.InterviewRoom,
  })),
);

// Public interview link: anyone with the scope's slug can start an interview.
export const Route = createFileRoute("/interview/$slug")({
  component: PublicInterviewPage,
});

function PublicInterviewPage() {
  const { slug } = Route.useParams();
  // All query params the recruitment service appended to the link (e.g. a participant_id plus any
  // study/session ids). InterviewRoom reads the configured ID param and passes the rest as
  // passthrough metadata; read once from location.search (the public link has no typed search schema).
  // `?lang=` is ours (it preselects the interview language when the link offers more than one);
  // InterviewRoom reads it and strips it before the rest is stored as recruitment metadata.
  const urlParams = Object.fromEntries(new URLSearchParams(window.location.search));
  // One key per start attempt, kept across a failed retry so a lost response cannot mint a
  // second session (see StartAttempts).
  const [attempts] = useState(() => new StartAttempts());
  return (
    // Participant-facing: one plain message covers both a chunk that failed to load and a render
    // crash inside the room (e.g. browser auto-translate), with Reload as the way out.
    <LazyBoundary name="InterviewRoomChunk" label="Something went wrong">
      <InterviewRoom
        urlParams={urlParams}
        start={async (answers, participantId, metadata, consent, language, scopeToken) => {
          try {
            const started = await startPublicInterview(
              slug,
              requireScopeToken(scopeToken),
              answers,
              participantId,
              metadata,
              consent,
              language,
              attempts.current()
            );
            attempts.succeeded();
            return started;
          } catch (error) {
            attempts.failed(error);
            throw error;
          }
        }}
        checkAnswer={(qid, selected, scopeToken) =>
          checkScreenerAnswer(slug, requireScopeToken(scopeToken), qid, selected)
        }
        loadInfo={() => getPublicStudy(slug)}
      />
    </LazyBoundary>
  );
}

function requireScopeToken(token: string | undefined): string {
  if (!token) {
    throw new InterviewError(400, "Refresh this interview page and try again.");
  }
  return token;
}
